import React from 'react';
import useStyledTheme from 'shared/hooks/use-styled-theme';
import RadioCheckedIcon from 'shared/assets/radio-checked';
import RadioUncheckedIcon from 'shared/assets/radio-unchecked';
import { TabValue } from 'src/checkout/components/sections/payment/shared/context';
import {
  AlertContainer,
  Badge,
  BadgeContent,
  BadgeLabel,
  RadioContainer,
  Tab,
  TabContainer,
  TabContent,
  TabDisabledReason,
  TabLabel,
  TabList,
  TabListSection,
  TabsContainer,
  TabText,
  TabTextGroup,
} from './payment-tabs.styles';
import { PaymentBanner } from '../../shared/banner';
import { TabCallout, TabCalloutProps } from './tab-callout';

export type TabOption = {
  value: TabValue;
  label: string;
  sublabel?: string;
  badgeLabel: string;
  badgeIcon?: React.ReactNode;
  hasError?: boolean;
  callout?: TabCalloutProps;
  disabled?: boolean;
};

type PaymentTabsProps = {
  children: React.ReactNode;
  onTabChange: (tab: string) => void;
  selectedTab: TabValue;
  tabOptions: TabOption[];
  showFeeBadgeInline?: boolean;
  showEmbeddedSearch?: boolean;
  alertMessage?: string;
};

const hasTabSelected = (selectedTab: TabValue): boolean => selectedTab !== TabValue.none;

const RadioIndicator = ({ selected, color }: { selected: boolean; color?: string }): JSX.Element => (
  <RadioContainer>{selected ? <RadioCheckedIcon color={color} /> : <RadioUncheckedIcon />}</RadioContainer>
);

const FeeBadge = ({ badgeLabel, badgeIcon }: { badgeLabel: string; badgeIcon?: React.ReactNode }): JSX.Element => (
  <Badge>
    <BadgeContent>
      {!!badgeIcon && badgeIcon}
      <BadgeLabel>{badgeLabel}</BadgeLabel>
    </BadgeContent>
  </Badge>
);

export const PaymentTabs: React.FC<PaymentTabsProps> = ({
  selectedTab,
  onTabChange,
  children,
  tabOptions,
  showFeeBadgeInline = false,
  showEmbeddedSearch = false,
  alertMessage,
}) => {
  const theme = useStyledTheme() as {
    customized?: { colors?: { buttonsLinks?: string } };
    colors?: { primaryBlue?: string };
  };
  const accentColor: string = theme.customized?.colors?.buttonsLinks ?? theme.colors?.primaryBlue ?? '';
  const showNoSelectionError = !!alertMessage && !hasTabSelected(selectedTab);

  // Next enabled tab index in the given direction, skipping disabled tabs.
  const getNextEnabledIndex = (fromIndex: number, step: number): number => {
    const totalTabs = tabOptions.length;
    for (let offset = 1; offset <= totalTabs; offset++) {
      const index = (((fromIndex + step * offset) % totalTabs) + totalTabs) % totalTabs;
      if (!tabOptions[index].disabled) {
        return index;
      }
    }
    return fromIndex;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = tabOptions.findIndex((tab) => tab.value === selectedTab);

    const keyMap: Partial<Record<string, number>> = {
      arrowright: getNextEnabledIndex(currentIndex, 1),
      arrowleft: getNextEnabledIndex(currentIndex, -1),
    };

    const newIndex = keyMap[e.key.toLowerCase()];
    if (newIndex !== undefined && newIndex !== currentIndex) {
      e.preventDefault();
      onTabChange(tabOptions[newIndex].value);
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const focusedTabIndex = tabOptions.findIndex((tab) => document.activeElement?.id === `tab-${tab.value}`);
      if (focusedTabIndex !== -1 && !tabOptions[focusedTabIndex].disabled) {
        onTabChange(tabOptions[focusedTabIndex].value);
      }
    }
  };

  const firstTabValue = tabOptions[0]?.value ?? '';
  const firstEnabledTabIndex = tabOptions.findIndex((tab) => !tab.disabled);

  return (
    <TabsContainer data-testid='payment-tabs' showEmbeddedSearch={showEmbeddedSearch}>
      <TabListSection>
        {!!alertMessage && (
          <AlertContainer isEmbeddedSearch={showEmbeddedSearch}>
            <PaymentBanner description={alertMessage} severity='errorV2' />
          </AlertContainer>
        )}
        <TabList role='tablist'>
          {tabOptions.map(
            ({ value, label, sublabel, badgeLabel, badgeIcon, hasError: tabHasError, callout, disabled }, index) => {
              const isSelected = selectedTab === value;
              const isFirstTab = index === firstEnabledTabIndex;
              const isFocusable = !disabled && (isSelected || (!hasTabSelected(selectedTab) && isFirstTab));

              return (
                <TabContainer key={value}>
                  {!!callout && <TabCallout {...callout} />}
                  <Tab
                    role='tab'
                    id={`tab-${value}`}
                    aria-controls={`tabpanel-${value}`}
                    aria-selected={isSelected}
                    aria-disabled={disabled ? true : undefined}
                    tabIndex={isFocusable ? 0 : -1}
                    selected={isSelected}
                    isDisabled={!!disabled}
                    error={!disabled && (showNoSelectionError || tabHasError)}
                    onClick={() => !disabled && onTabChange(value)}
                    onKeyDown={handleKeyDown}
                    accentColor={accentColor}
                  >
                    <RadioIndicator selected={isSelected} color={accentColor} />
                    <TabLabel>
                      <TabTextGroup gap={showFeeBadgeInline ? 2 : 1} isStacked={!!disabled}>
                        <TabText size='medium' weight={isSelected ? 'semi' : 'regular'} selected={isSelected}>
                          {label}
                        </TabText>
                        {!!sublabel &&
                          (disabled ? (
                            <TabDisabledReason size='small' selected={false}>
                              {sublabel}
                            </TabDisabledReason>
                          ) : (
                            <TabText size='medium' selected={isSelected}>
                              {sublabel}
                            </TabText>
                          ))}
                        {!!badgeLabel && showFeeBadgeInline && (
                          <FeeBadge badgeLabel={badgeLabel} badgeIcon={badgeIcon} />
                        )}
                      </TabTextGroup>
                      {!!badgeLabel && !showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} />}
                    </TabLabel>
                  </Tab>
                </TabContainer>
              );
            }
          )}
        </TabList>
      </TabListSection>

      {hasTabSelected(selectedTab) && (
        <TabContent
          role='tabpanel'
          id={`tabpanel-${selectedTab}`}
          aria-labelledby={`tab-${selectedTab}`}
          tabIndex={0}
          selectedTab={selectedTab}
          isFirst={selectedTab === firstTabValue}
          error={tabOptions.some((tab) => tab.hasError)}
          showEmbeddedSearch={showEmbeddedSearch}
        >
          {children}
        </TabContent>
      )}
    </TabsContainer>
  );
};
